import { useEffect, useMemo, useRef, useState } from "react";
import { motion, useAnimate } from "framer-motion";

type Pt = { x: number; y: number };

function pathUpRight(from: Pt, to: Pt) {
  const kneeY = Math.min(from.y, to.y) - 30;
  return `
    M ${from.x} ${from.y}
    L ${from.x} ${kneeY}
    L ${to.x - 50} ${kneeY}

  `;
}

function pathDownRight(from: Pt, to: Pt) {
  const kneeY = Math.max(from.y, to.y - 70);
  return `
    M ${from.x} ${from.y}
    L ${from.x} ${kneeY}
    L ${to.x + 90} ${kneeY}
        L ${to.x + 90} ${kneeY + 35}

  `;
}

export default function HomeHubCardsV4Responsive() {
  const [scope, animate] = useAnimate<HTMLDivElement>();
  const ranRef = useRef(false);

  //  디자인 기준 좌표계(여기만 “기준값”으로 유지)
  const DESIGN_W = 720;
  const DESIGN_H = 720;

  //  실제 렌더 폭(반응형): 컨테이너 폭에 맞춰 scale 계산
  const wrapRef = useRef<HTMLDivElement | null>(null);
  const [wrapW, setWrapW] = useState(0);

  useEffect(() => {
    if (!wrapRef.current) return;

    const ro = new ResizeObserver(([entry]) => {
      const w = entry?.contentRect?.width ?? 0;
      setWrapW(w);
    });
    ro.observe(wrapRef.current);

    return () => ro.disconnect();
  }, []);

  const scale = wrapW > 0 ? wrapW / DESIGN_W : 1;
  const wrapH = DESIGN_H * scale;

  //  카드 이미지
  const leftBottomImg = "/assets/images/home/card4_1.png";
  const topImg = "/assets/images/home/card4_2.png";
  const rightBottomImg = "/assets/images/home/card4_3.png";

  //  카드 배치 (디자인 좌표계 기준!)
  const card = {
    leftBottom: { x: 80, y: 300, w: 253.899, h: 239.0552 },
    top: { x: 280, y: 135, w: 260, h: 150 },
    rightBottom: { x: 320, y: 460, w: 280, h: 150 },
  };

  //  “첫 카드 왼쪽 35%” 시작점
  const attach = useMemo(() => {
    return {
      lb: {
        x: card.leftBottom.x + card.leftBottom.w * 0.35,
        y: card.leftBottom.y + card.leftBottom.h * 0.15,
      },
      top: {
        x: card.top.x + card.top.w * 0.35,
        y: card.top.y + card.top.h * 0.9, // 🔥 여기만 0.6 → 0.5
      },
      rb: {
        x: card.rightBottom.x + card.rightBottom.w * 0.15,
        y: card.rightBottom.y + card.rightBottom.h * 0.25,
      },
    };
  }, []);

  const d1 = useMemo(() => pathUpRight(attach.lb, attach.top), [attach]);
  const d2 = useMemo(() => pathDownRight(attach.top, attach.rb), [attach]);

  const stroke = "rgba(17,24,39,0.20)";
  const strokeWidth = 2;

  const dotStyle = (d: string) =>
    ({
      position: "absolute",
      width: 7,
      height: 7,
      borderRadius: 999,
      background: "#2563EB",
      boxShadow: "0 8px 16px rgba(37,99,235,0.22)",
      offsetPath: `path('${d}')`,
      offsetDistance: "0%",
      offsetRotate: "0deg",
      left: 0,
      top: 0,
      pointerEvents: "none",
    }) as React.CSSProperties;

  useEffect(() => {
    let alive = true;
    const el = scope.current;
    if (!el) return;

    const run = async () => {
      if (ranRef.current) return;
      ranRef.current = true;

      await animate(".card-lb", { opacity: 0, y: 8, scale: 0.985 }, { duration: 0 });
      await animate(".card-top", { opacity: 0, y: 8, scale: 0.985 }, { duration: 0 });
      await animate(".card-rb", { opacity: 0, y: 8, scale: 0.985 }, { duration: 0 });
      const CARD_DUR = 0.9;
      const EASE = [0.16, 1, 0.3, 1] as const;
      await animate(".line-1", { pathLength: 0, opacity: 0 }, { duration: 0 });
      await animate(".dot-1", { opacity: 0, scale: 0.6 }, { duration: 0 });
      await animate(".dot-1", { offsetDistance: "0%" } as any, { duration: 0 });

      await animate(".line-2", { pathLength: 0, opacity: 0 }, { duration: 0 });
      await animate(".dot-2", { opacity: 0, scale: 0.6 }, { duration: 0 });
      await animate(".dot-2", { offsetDistance: "0%" } as any, { duration: 0 });

      if (!alive) return;

      await animate(".card-lb", { opacity: 1, y: 0, scale: 1 }, { duration: CARD_DUR, ease: EASE });

      await Promise.all([
        animate(
          ".line-1",
          { pathLength: 1, opacity: 1 },
          { duration: 0.7, ease: [0.22, 1, 0.36, 1] },
        ),
        animate(".dot-1", { opacity: 1, scale: 1 }, { duration: 0.18, ease: [0.22, 1, 0.36, 1] }),
        animate(".dot-1", { offsetDistance: "100%" } as any, {
          duration: 0.7,
          ease: [0.22, 1, 0.36, 1],
        }),
      ]);

      await animate(
        ".card-top",
        { opacity: 1, y: 0, scale: 1 },
        { duration: CARD_DUR, ease: EASE },
      );

      await Promise.all([
        animate(
          ".line-2",
          { pathLength: 1, opacity: 1 },
          { duration: 0.65, ease: [0.22, 1, 0.36, 1] },
        ),
        animate(".dot-2", { opacity: 1, scale: 1 }, { duration: 0.18, ease: [0.22, 1, 0.36, 1] }),
        animate(".dot-2", { offsetDistance: "100%" } as any, {
          duration: 0.65,
          ease: [0.22, 1, 0.36, 1],
        }),
      ]);

      await animate(".card-rb", { opacity: 1, y: 0, scale: 1 }, { duration: CARD_DUR, ease: EASE });
    };

    const io = new IntersectionObserver(([entry]) => entry?.isIntersecting && run(), {
      threshold: 0.45,
      rootMargin: "0px 0px -10% 0px",
    });

    io.observe(el);
    return () => {
      alive = false;
      io.disconnect();
    };
  }, [animate, scope]);

  return (
    //  바깥은 반응형 폭
    <div ref={wrapRef} style={{ width: "100%" }}>
      {/*  실제 렌더 높이는 scale로 계산 */}
      <div
        ref={scope}
        style={{
          width: "100%",
          height: wrapH,
          position: "relative",
          borderRadius: 24,
          background: "#F6F7FB",
          overflow: "hidden",
        }}
      >
        <img
          loading="lazy"
          src="/assets/images/home/card_bg.png"
          alt=""
          draggable={false}
          style={{
            position: "absolute",
            inset: 0,
            width: "100%",
            height: "100%",
            objectFit: "cover", // 이미지가 꽉 차게
            pointerEvents: "none",
            userSelect: "none",
            zIndex: 0,
          }}
        />
        {/*  SVG는 viewBox로 스케일 */}
        <svg
          viewBox={`0 0 ${DESIGN_W} ${DESIGN_H}`}
          preserveAspectRatio="xMidYMid meet"
          width="100%"
          height="100%"
          style={{ position: "absolute", inset: 0 }}
        >
          <motion.path
            className="line-1"
            d={d1}
            fill="none"
            stroke={stroke}
            strokeWidth={strokeWidth}
            strokeLinecap="round"
            initial={{ pathLength: 0, opacity: 0 }}
          />
          <motion.path
            className="line-2"
            d={d2}
            fill="none"
            stroke={stroke}
            strokeWidth={strokeWidth}
            strokeLinecap="round"
            initial={{ pathLength: 0, opacity: 0 }}
          />
        </svg>

        {/*  카드/도트는 디자인 좌표계대로 놓고, 한 번에 scale로 줄여줌 */}
        <div
          style={{
            position: "absolute",
            left: 0,
            top: 0,
            width: DESIGN_W,
            height: DESIGN_H,
            transformOrigin: "top left",
            transform: `scale(${scale})`,
          }}
        >
          <motion.div initial={{ opacity: 0, scale: 0.6 }} className="dot-1" style={dotStyle(d1)} />
          <motion.div initial={{ opacity: 0, scale: 0.6 }} className="dot-2" style={dotStyle(d2)} />

          <motion.img
            initial={{ opacity: 0, y: 12, scale: 0.96 }} //
            className="card-lb"
            src={leftBottomImg}
            alt=""
            draggable={false}
            style={{
              position: "absolute",
              left: card.leftBottom.x,
              top: card.leftBottom.y,
              width: card.leftBottom.w,
              height: "auto",
              userSelect: "none",
              filter: "drop-shadow(0 14px 30px rgba(0,0,0,0.12))",
            }}
          />

          <motion.img
            initial={{ opacity: 0, y: 12, scale: 0.96 }} //
            className="card-top"
            src={topImg}
            alt=""
            draggable={false}
            style={{
              position: "absolute",
              left: card.top.x,
              top: card.top.y,
              width: card.top.w,
              height: "auto",
              userSelect: "none",
              filter: "drop-shadow(0 14px 30px rgba(0,0,0,0.12))",
            }}
          />

          <motion.img
            initial={{ opacity: 0, y: 12, scale: 0.96 }} //
            className="card-rb"
            src={rightBottomImg}
            alt=""
            draggable={false}
            style={{
              position: "absolute",
              left: card.rightBottom.x,
              top: card.rightBottom.y,
              width: card.rightBottom.w,
              height: "auto",
              userSelect: "none",
              filter: "drop-shadow(0 14px 30px rgba(0,0,0,0.12))",
            }}
          />
        </div>
      </div>
    </div>
  );
}
